import {JobNodes} from "@cm/job-nodes/job-nodes"
import {imageProcessingTask} from "@cm/job-nodes/image-processing"
import type {InputMapsInfo, MaterialDetails} from "#material-maps-exporter/core/types"
import {DataObjectAssignmentTypeStringToNumber} from "#material-maps-exporter/core/types"
import {
    createConfigFieldUpdateOpForDataObjectReference,
    materialMapsExporterUpdateExportConfigTask,
    createConfigStructFieldUpdateOp,
} from "#material-maps-exporter/targets/pbr/update-export-config"
import {Utility} from "@cm/job-nodes/utility"
import type {Config, UpdateOp} from "#material-maps-exporter/targets/pbr/types"
import type {ThumbnailOptions} from "@cm/job-nodes"
import {generateThumbnailsDataObjectTask} from "@cm/job-nodes"
import {imageProcessingGraphForPbrConversionConfig} from "#material-maps-exporter/targets/pbr/image-processing-graph"
import {useLinearSpace} from "#material-maps-exporter/targets/pbr/export-config"
import {DataObjectAssignmentType} from "@cm/utils/data-object-assignment"

// TODO: simplify, we don't need generic traversal since structure of export is predefined
export function jobGraphFnForPbrExportConfig(
    config: Config,
    mapsInfo: InputMapsInfo,
    materialDetails: MaterialDetails,
    platformVersion: string,
    zipFilename: string | null = null,
) {
    const exportConfigUpdateOps: JobNodes.TypedDataNode<UpdateOp>[] = [JobNodes.value(createConfigStructFieldUpdateOp(config, config, "state", "done"))]

    const traverse = (configFolder: Config["root"]): Utility.Zip.JobNodes.Folder => {
        const content: (typeof Utility.Zip.JobNodes)["folder"] extends (content: infer A) => unknown ? A : never = {}
        for (const item of configFolder.content) {
            if (item.type === "conversionConfig") {
                const imageProcessingGraph = imageProcessingGraphForPbrConversionConfig(item, mapsInfo)
                const imgProcTask = JobNodes.task(imageProcessingTask, {
                    input: JobNodes.value({graph: imageProcessingGraph}),
                })
                for (const mapEntry of item.maps) {
                    const imageColorSpace = useLinearSpace(item, mapEntry.name) ? "LINEAR" : "SRGB"
                    const thumbnailsTask = JobNodes.task(generateThumbnailsDataObjectTask, {
                        input: JobNodes.struct({
                            dataObject: JobNodes.get(
                                Utility.DataObject.update(JobNodes.get(imgProcTask, mapEntry.name), {
                                    imageColorSpace: imageColorSpace,
                                }),
                                "dataObject",
                            ),
                            // we don't really want to generate any thumbnails here, we just want to update the color space in the image which the thumbnail service does as well
                            options: JobNodes.value({
                                generateTiles: false,
                                generateThumbnails: [] as unknown as ThumbnailOptions["generateThumbnails"],
                            }),
                        }),
                    })
                    content[mapEntry.filename] = Utility.Zip.JobNodes.file(JobNodes.get(thumbnailsTask, "dataObject"))
                }
            } else if (item.type === "exportJsonFile") {
                content[item.name] = Utility.Zip.JobNodes.file(Utility.Zip.JobNodes.json(item.content))
            } else if (item.type === "exportTextFile") {
                content[item.name] = Utility.Zip.JobNodes.file(Utility.Zip.JobNodes.text(item.content))
            } else if (item.type === "exportFolder") {
                content[item.name] = traverse(item)
            } else {
                throw Error(`Unknown type in export config to job graph transform: ${item["type"]}`)
            }
        }
        return Utility.Zip.JobNodes.folder(content)
    }

    const zipRoot = traverse(config.root)
    const zipTask = JobNodes.task(Utility.Zip.task, {
        input: JobNodes.struct({
            filename: JobNodes.value(zipFilename ?? config.root.name),
            destination: JobNodes.value({
                type: "taskOutputOnly",
                customerId: materialDetails.organization.legacyId,
            } as Utility.Zip.Destination),
            root: zipRoot,
        }),
    })

    exportConfigUpdateOps.push(
        createConfigFieldUpdateOpForDataObjectReference(
            config,
            config,
            "output",
            JobNodes.get(zipTask, "zipFile"),
            DataObjectAssignmentTypeStringToNumber[DataObjectAssignmentType.MaterialMapsExport],
        ),
    )

    return (jsonObjectId: number) => {
        const updateConfigTask = JobNodes.task(materialMapsExporterUpdateExportConfigTask, {
            input: JobNodes.struct({
                jsonObjectId: JobNodes.value(jsonObjectId),
                updateOps: JobNodes.list(exportConfigUpdateOps),
            }),
        })

        return JobNodes.jobGraph(JobNodes.list([updateConfigTask, zipTask]), {platformVersion})
    }
}
